// src/admin/screens/loans/actions/MarkDisbursedModal.tsx
// Manual disbursement recording — use when funds were sent via mobile money
// out-of-band (e.g., staff disbursement before the aggregator integration
// lands, or for the disbursement_failed → mark_disbursed recovery path).
// Schema notes: payments.direction uses 'disbursement', provider is a free
// string (we feed the mobile-money network name), provider_ref is the
// aggregator/USSD reference, completed_at is when funds left. user_id is
// the *borrower*'s auth uid (from loans.user_id); idempotency_key is a
// per-attempt uuid so retries don't double-post.
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import { kwachaStringToNgwee } from '@admin/utils/money';
import type { LoanDetail } from '@admin/hooks/useLoan';

const schema = z.object({
  provider_ref: z.string().min(3, 'Reference required'),
  provider: z.enum(['mtn_momo', 'airtel_money', 'zamtel_kwacha']),
  disbursed_at: z.string().min(10, 'Date required'),
  amount: z.string().min(1, 'Amount required'),
});
type Values = z.infer<typeof schema>;

interface Props {
  loan: LoanDetail;
  onClose: (refresh: boolean) => void;
}

export default function MarkDisbursedModal({ loan, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      provider: 'mtn_momo',
      disbursed_at: new Date().toISOString().slice(0, 16),
      amount: (loan.principal_ngwee / 100).toFixed(2),
      provider_ref: '',
    },
  });

  const submit = async (v: Values) => {
    const ngwee = kwachaStringToNgwee(v.amount);
    if (ngwee == null || ngwee <= 0) {
      toast({ kind: 'error', msg: 'Invalid amount.' });
      return;
    }
    setBusy(true);
    try {
      const completedIso = new Date(v.disbursed_at).toISOString();
      const { error: payErr } = await supabase.from('payments').insert({
        loan_id: loan.id,
        user_id: loan.user_id,
        direction: 'disbursement',
        status: 'completed',
        amount_ngwee: ngwee,
        provider: v.provider,
        provider_ref: v.provider_ref,
        idempotency_key: `manual-disburse-${loan.id}-${Date.now()}`,
        initiated_at: completedIso,
        completed_at: completedIso,
      });
      if (payErr) throw payErr;
      const { error: lnErr } = await supabase
        .from('loans')
        .update({
          status: 'active',
          disbursed_at: completedIso,
        })
        .eq('id', loan.id);
      if (lnErr) throw lnErr;
      await recordAudit({
        action: 'loan.mark_disbursed',
        target_type: 'loan',
        target_id: loan.id,
        payload: {
          provider_ref: v.provider_ref,
          provider: v.provider,
          amount_ngwee: ngwee,
        },
      });
      toast({ kind: 'success', msg: `${loan.loan_number} marked disbursed` });
      await qc.invalidateQueries({ queryKey: ['loan', loan.id] });
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onClose(false)}
      title={`Mark ${loan.loan_number} disbursed`}
    >
      <form
        onSubmit={handleSubmit(submit)}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <p className="small">
          Use after sending the funds via mobile money. Enter the provider
          reference — it is stored and surfaced on reconciliation.
        </p>
        <FormField
          label="Provider reference"
          htmlFor="pref"
          error={errors.provider_ref?.message}
          required
        >
          <input id="pref" {...register('provider_ref')} />
        </FormField>
        <FormField label="Network" htmlFor="provider" required>
          <select id="provider" {...register('provider')}>
            <option value="mtn_momo">MTN MoMo</option>
            <option value="airtel_money">Airtel Money</option>
            <option value="zamtel_kwacha">Zamtel Kwacha</option>
          </select>
        </FormField>
        <FormField
          label="Amount (K)"
          htmlFor="amt"
          error={errors.amount?.message}
          required
        >
          <input id="amt" inputMode="decimal" {...register('amount')} />
        </FormField>
        <FormField
          label="Disbursed at"
          htmlFor="dat"
          error={errors.disbursed_at?.message}
          required
        >
          <input id="dat" type="datetime-local" {...register('disbursed_at')} />
        </FormField>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onClose(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            Mark disbursed
          </Button>
        </div>
      </form>
    </Modal>
  );
}
